import { Injectable, Inject } from '@angular/core';
import { ApiService } from '../api.service';
import { Observable } from 'rxjs';
import { SessionService } from '../session.service';
import { map } from 'rxjs/operators';
import { CustomerSearchService, CustomerRosterSearchPayload } from '../shared/services/customer-search.service';

export interface LumaCampaign {
  id: string;
  name: string;
  status: 'pending' | 'processing' | 'generation_complete' | 'pending_send' | 'sending' | 'send_complete' | 'failed';
  created_at?: string;
  updated_at?: string;
  created_by?: number;
  created_by_user?: { id: number; first_name?: string; last_name?: string; email?: string };
  updated_by?: number;
  updated_by_user?: { id: number; first_name?: string; last_name?: string; email?: string };
  total_recipients?: number;
  generated_count?: number;
  sent_count?: number;
}

export interface CampaignRecipient {
  id: string;
  account_app_user_id: number;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  generated_subject: string | null;
  generated_body: string | null;
  created_at: string;
  updated_at?: string;
  updated_by?: number | null;
  updated_by_first_name?: string | null;
  updated_by_last_name?: string | null;
  updated_by_email?: string | null;
}

@Injectable({ providedIn: 'root' })
export class LumaService {
  private readonly PATH = 'luma';
  constructor(private api: ApiService, @Inject(SessionService) private session: SessionService, private customerSearch: CustomerSearchService) {}

  private accountAppPath(): string {
    const accountId = this.session.currentAccountValue!.accountID;
    const appId = this.session.currentAppValue!.id;
    return `${this.PATH}/${accountId}/${appId}`;
  }

  listCampaigns(): Observable<LumaCampaign[]> {
    return this.api.get<LumaCampaign[]>(`${this.accountAppPath()}/campaigns`);
  }

  getCampaign(id: string): Observable<LumaCampaign> {
    return this.api.get<LumaCampaign>(`${this.accountAppPath()}/campaigns/${id}`);
  }

  createCampaign(payload: any): Observable<LumaCampaign> {
    return this.api.post<LumaCampaign>(`${this.accountAppPath()}/campaigns`, payload);
  }

  updateCampaign(id: string, payload: any): Observable<LumaCampaign> {
    return this.api.put<LumaCampaign>(`${this.accountAppPath()}/campaigns/${id}`, payload);
  }

  duplicateCampaign(id: string): Observable<LumaCampaign> {
    return this.api.post<LumaCampaign>(`${this.accountAppPath()}/campaigns/${id}/duplicate`, {});
  }

  deleteCampaign(id: string): Observable<void> {
    return this.api.delete<void>(`${this.accountAppPath()}/campaigns/${id}`);
  }

  countAudience(criteria: any): Observable<number> {
    const mode = criteria?.audienceMode;
    if (mode === 'saved' && criteria?.savedSearchId) {
      return this.customerSearch.runSavedSearch(criteria.savedSearchId).pipe(map((r: any) => r?.count ?? 0));
    }
    if (mode === 'search' && criteria?.searchQuery) {
      const payload: CustomerRosterSearchPayload = {
        ...(criteria.searchQuery as CustomerRosterSearchPayload),
      } as any;
      // Ensure pagination doesn't cap count
      payload.query = payload.query || {} as any;
      (payload.query as any).pagination = { page: 0, per_page: 1 };
      return this.customerSearch.runSearch(payload).pipe(map((r: any) => r?.count ?? 0));
    }
    // Default: All Customers — registered users only, latest device per user
    const accountId = this.session.currentAccountValue!.accountID;
    const appId = this.session.currentAppValue!.id;
    const payload = this.customerSearch.buildBasePayload({
      context: { account_id: accountId, account_app_id: appId, mode: { registration: 'registered', devices: 'latest_per_user' } },
      query: {
        pagination: { page: 0, per_page: 1 },
        where: { field: 'user.email', type: 'String', op: 'not_null' }
      }
    });
    return this.customerSearch.runSearch(payload).pipe(map((r: any) => r?.count ?? 0));
  }

  getProfileFields(): Observable<string[]> {
    return this.api.get(`${this.accountAppPath()}/profile-fields`).pipe(
      map((profile: any) => {
        const fields: string[] = [];
        // Expect { User: { fields: [ { id, fields? } ] } }
        const root = profile?.User?.fields || [];
        const walk = (prefix: string, items: any[]) => {
          for (const f of items) {
            if (Array.isArray(f.fields) && f.fields.length) {
              walk(prefix ? `${prefix}.${f.id}` : f.id, f.fields);
            } else {
              fields.push(prefix ? `${prefix}.${f.id}` : f.id);
            }
          }
        };
        walk('', root);
        return fields;
      })
    );
  }

  generatePreview(payload: any): Observable<{ subject?: string; body?: string }> {
    const accountId = this.session.currentAccountValue!.accountID;
    const appId = this.session.currentAppValue!.id;
    return this.api.post<{ subject?: string; body?: string }>(`luma/${accountId}/${appId}/preview`, payload);
  }

  downloadZip(id: string, format?: 'csv' | 'xlsx' | 'eml'): void {
    const qp = format ? `?format=${format}` : '';
    this.api.download(`${this.accountAppPath()}/campaigns/${id}/download${qp}`);
  }

  listRecipients(campaignId: string, page = 0, perPage = 25): Observable<{ records: CampaignRecipient[]; page: number; per_page: number; count: number }> {
    return this.api.get<{ records: CampaignRecipient[]; page: number; per_page: number; count: number }>(`${this.accountAppPath()}/campaigns/${campaignId}/recipients?page=${page}&per_page=${perPage}`);
  }

  getRecipient(campaignId: string, recipientId: string): Observable<CampaignRecipient> {
    return this.api.get<CampaignRecipient>(`${this.accountAppPath()}/campaigns/${campaignId}/recipients/${recipientId}`);
  }

  updateRecipient(campaignId: string, recipientId: string, body: { generated_subject: string | null; generated_body: string | null }): Observable<CampaignRecipient> {
    return this.api.put<CampaignRecipient>(`${this.accountAppPath()}/campaigns/${campaignId}/recipients/${recipientId}`, body);
  }

  setSender(campaignId: string, body: { sender_name: string; sender_email: string }): Observable<LumaCampaign> {
    return this.api.post<LumaCampaign>(`${this.accountAppPath()}/campaigns/${campaignId}/sender`, body);
  }

  isSendgridEnabled(): Observable<{ enabled: boolean }> {
    return this.api.get<{ enabled: boolean }>(`${this.accountAppPath()}/integrations/sendgrid-enabled`);
  }
}


